import { Alert, Button, Input } from '30pay/components';
import { DepositNetwork, Enum, NetworksApiResponse, NetworksTypes } from 'interfaces';
import { useEffect, useRef, useState } from 'react';
import { BsChevronDown } from 'react-icons/bs';
import { copyToClipboard, get, post, priceDigit } from 'scripts';
import { Card, CoinIcon } from 'utilities/components';
import { HiArrowLeft, HiArrowRight } from 'react-icons/hi2';
import { QRCode } from 'antd';
import { API } from 'data/30pay.api';
import { TbCopy } from 'react-icons/tb';
import { SelectNetworkModal } from '../buyWithdrawMethod/modal/selectNetworksModal.index';
import { __ConvertBoxConfirmOrderDetailModalRef } from '../confirmOrderModal/orderDetailModal.interface';
import { ConvertBoxDataType } from '../convertBox.index';
import { __TypeOfOrderEnum } from '../initial/initial.interface';
import ConvertBoxConfirmOrderModal from '30pay/trade/convertBox/confirmOrderModal/orderDetailModal.index';
import { __SellCryptoTxIdConvertBoxProps } from './sellCryptoTxId.interface';
import { __SellWithdrawMethodsEnums } from '../sellWithdrawMethod/sellWithdrawMethod.interface';

const SellCryptoTxId: React.FC<__SellCryptoTxIdConvertBoxProps> = (props: __SellCryptoTxIdConvertBoxProps) => {
    const [networksLoading, setNetworksLoading] = useState(true);
    const [networks, setNetworks] = useState<DepositNetwork[]>();
    const [selectedNetwork, setSelectedNetwork] = useState<DepositNetwork>();
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [txid, setTxid] = useState<string | undefined>();
    const [confirmData, setConfirmData] = useState<ConvertBoxDataType>();
    const confirmModalRef = useRef<__ConvertBoxConfirmOrderDetailModalRef>(null);
    const NoTXID = ['TRX', 'USDT'];
    useEffect(() => {
        setNetworksLoading(true);
        const isSell = props.data?.typeOfOrder === __TypeOfOrderEnum.sell;
        const targetCoin = isSell ? props.data?.srcCoin?.symbol : props.data?.destCoin?.symbol;
        if (targetCoin) {
            get<{ addressWallets: DepositNetwork[] }>(API.market.depositNetworks(targetCoin.toLowerCase()))
                .then((e) => {
                    const networks = e.value.value.addressWallets;
                    setNetworks(networks);
                    setSelectedNetwork(networks.find(network => network.network === props.data?.network?.network) || networks[0]);
                })
                .catch((e) => console.log(e))
                .finally(() => setNetworksLoading(false));
        } else {
            setNetworksLoading(false);
            setNetworks([]);
        }
    }, []);

    function isNeedTxId() {
        return !NoTXID.includes(props.data?.srcCoin?.symbol || '');
    }

    function submitHandler() {
        confirmModalRef.current?.startLoading();
        const { data } = props;
        const body = {
            trackingCode: data?.trackingCode,
            txid,
            iBanNumberId: data?.withdrawMethod === __SellWithdrawMethodsEnums.iBan ? data?.bankAccountId : undefined,
            destination: data?.withdrawMethod,
        };
        const api = isNeedTxId() ? API.orderTransaction.setTxId : API.orderTransaction.searchTxId;
        post<{ trackingCode: string; sourceAmount: number; state: Enum; destinationAmount: number }>(api, body)
            .then((res) => {
                confirmModalRef.current?.showResultContent(true, res.value.value.trackingCode, {
                    sourceAmount: res.value.value.sourceAmount,
                    state: res.value.value.state,
                    destinationAmount: res.value.value.destinationAmount,
                });
            })
            .catch((err) => console.log(err))
            .finally(() => confirmModalRef.current?.stopLoading());
    }

    function backHandler() {
        props.onBack();
    }

    return (
        <div className="flex max-lg:flex-col gap-6 h-full">
            <ConvertBoxConfirmOrderModal
                ref={confirmModalRef}
                data={confirmData}
                isOpen={!!confirmData}
                onClose={(e) => {
                    setConfirmData(undefined);
                    if (e) props.onFinish();
                }}
                submitButton={{
                    title: 'ثبت سفارش',
                    onClick: submitHandler,
                }}
            />
            {networks && (
                <SelectNetworkModal
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    networks={networks}
                    onSelect={(network: any) => {
                        setSelectedNetwork(network);
                        setModalOpen(false);
                    }}
                />
            )}
            <Card loading={networksLoading} className="flex-1" title="واریز ارز مبدا" titleDivider>
                {props.data?.srcCoin?.symbol && (
                    <div className="flex items-center gap-2 mb-2">
                        <label className="text-sm font-medium text-black-100">مقدار مورد نظر:</label>
                        <div className="flex items-center gap-1">
                            <p className="text-sm font-medium text-darkPurple-100">{priceDigit(props.data.srcValue)}</p>
                            <label className="text-sm font-medium text-darkPurple-100">{props.data.srcCoin.name}</label>
                            /
                            <label className="en text-sm font-medium text-darkPurple-100">
                                {props.data.srcCoin.symbol}
                            </label>
                            <CoinIcon className="!w-5 !h-5 mb-1" symbol={props.data?.srcCoin?.symbol} />
                        </div>
                    </div>
                )}
                {isNeedTxId() ? (
                    <div className="flex flex-col gap-6">
                        <div className="gap-2 flex flex-col">
                            <label className="font-bold text-base text-black-100 en text-right">:TXID</label>
                            <Input
                                className={txid && txid !== '' ? 'en' : ''}
                                value={txid}
                                onChange={(e) => setTxid(e.target.value)}
                                placeholder="بعد از واریز به حساب ۳۰ پی TXID را اینجا وارد نمایید "
                            />
                        </div>
                    </div>
                ) : (
                    <Alert
                        variant="info"
                        message="بعد از انتقال به ولت رو به رو روی دکمه ادامه کلیک کنید در صورت موفق بودن انتقال شما به صورت اتوماتیک تراکنش شما شناسایی خواهد شد و نیازی به ورود هش ندارید."
                    />
                )}
                <div className="flex items-center w-full gap-4 mt-6">
                    <Button
                        disabled={!selectedNetwork || (isNeedTxId() ? !txid || txid === '' : false)}
                        onClick={() => setConfirmData(props.data)}
                        className="w-full h-16"
                        type="primary"
                    >
                        <HiArrowRight />
                        ادامه
                    </Button>
                    <Button onClick={backHandler} className="w-full h-16 bg-background" type="text">
                        بازگشت
                        <HiArrowLeft />
                    </Button>
                </div>
            </Card>
            <Card loading={!selectedNetwork?.walletAddress} className="flex-1">
                <div className="flex flex-wrap gap-4 items-end">
                    {selectedNetwork?.walletAddress && (
                        <QRCode
                            className="min-w-[140px] min-h-[140px]"
                            bordered={false}
                            size={140}
                            value={selectedNetwork?.walletAddress}
                        />
                    )}
                    <div className="flex flex-col gap-2 flex-1 min-w-[200px]">
                        <label className="font-bold text-base text-black-100">آدرس کیف پول:</label>
                        <div className="w-full rounded-[28px] border border-primary-20 flex items-center p-2">
                            <p className="flex-1 flex items-center justify-center font-normal text-base text-black-100 break-all px-2 whitespace-nowrap overflow-hidden text-ellipsis en">
                                {selectedNetwork?.walletAddress ?? '-'}
                            </p>
                            <Button
                                onClick={() => copyToClipboard(selectedNetwork?.walletAddress, 'آدرس کیف پول')}
                                className="h-8 shrink-0"
                                type="primary"
                            >
                                <div className="flex items-center gap-1 text-white">
                                    <TbCopy />
                                    <span className="text-sm font-bold">کپی</span>
                                </div>
                            </Button>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
};
export default SellCryptoTxId;
